import React, { useState } from 'react';
import { Platform, ScrollView, Text, useWindowDimensions, View } from 'react-native';
import {
  ActivityIndicator,
  Button,
  Modal,
  Portal,
} from 'react-native-paper';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { Palette, RADIUS, TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import Badge, { BadgeTone } from './ui/Badge';
import stockCarteSimService from '../services/stockCarteSimService';
import {
  SimRechargeItemStatus,
  SimRechargeJob,
  SimRechargeJobStatus,
} from '../types';

interface SimRechargeProgressModalProps {
  visible: boolean;
  job: SimRechargeJob | null;
  creating: boolean;
  polling: boolean;
  pollingPaused: boolean;
  error: string | null;
  onDismiss: () => void;
  onResumePolling: () => void;
  onStop: () => void;
  onRetryFailed: (simIds: number[]) => void;
}

type IconName = React.ComponentProps<typeof MaterialCommunityIcons>['name'];

const JOB_LABELS: Record<SimRechargeJobStatus, string> = {
  PENDING: 'En file d’attente',
  IN_PROGRESS: 'Recharge en cours',
  COMPLETED: 'Recharge terminée',
  PARTIAL_FAILURE: 'Terminée avec des échecs',
  FAILED: 'Recharge échouée',
};

const JOB_TONES: Record<SimRechargeJobStatus, BadgeTone> = {
  PENDING: 'neutral',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  PARTIAL_FAILURE: 'warning',
  FAILED: 'danger',
};

const ITEM_LABELS: Record<SimRechargeItemStatus, string> = {
  PENDING: 'En file',
  IN_PROGRESS: 'En cours',
  SUCCEEDED: 'Réussie',
  FAILED: 'Échouée',
};

const ITEM_TONES: Record<SimRechargeItemStatus, BadgeTone> = {
  PENDING: 'neutral',
  IN_PROGRESS: 'info',
  SUCCEEDED: 'success',
  FAILED: 'danger',
};

const ITEM_ICONS: Record<SimRechargeItemStatus, IconName> = {
  PENDING: 'clock-outline',
  IN_PROGRESS: 'sync',
  SUCCEEDED: 'check',
  FAILED: 'close',
};

/** Fill color of the global progress bar, following the job outcome. */
const progressColor = (colors: Palette, status: SimRechargeJobStatus) => {
  switch (status) {
    case 'COMPLETED': return colors.success;
    case 'PARTIAL_FAILURE': return colors.warning;
    case 'FAILED': return colors.danger;
    default: return colors.primary;
  }
};

const isTerminal = (status?: SimRechargeJobStatus) =>
  status === 'COMPLETED' || status === 'PARTIAL_FAILURE' || status === 'FAILED';

export default function SimRechargeProgressModal({
  visible,
  job,
  creating,
  polling,
  pollingPaused,
  error,
  onDismiss,
  onResumePolling,
  onStop,
  onRetryFailed,
}: SimRechargeProgressModalProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const listHeight = Math.min(200, Math.max(96, Math.round(windowHeight * 0.22)));
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const terminal = isTerminal(job?.status);
  const completedCount = job ? job.succeededCount + job.failedCount : 0;
  const progress = job && job.totalCount > 0 ? completedCount / job.totalCount : 0;
  const failedSimIds = job?.items
    .filter((item) => item.status === 'FAILED')
    .map((item) => item.simId) ?? [];
  const canDismiss = !job || terminal || pollingPaused;

  const handleDownloadReport = async () => {
    if (!job) return;
    setDownloadingReport(true);
    setDownloadError(null);
    try {
      await stockCarteSimService.downloadRechargeReport(job.id);
    } catch (e: unknown) {
      const data = (e as { response?: { data?: { erreur?: string } | string } })?.response?.data;
      let message = 'Impossible de télécharger le rapport.';
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data) as { erreur?: string };
          message = parsed.erreur || data;
        } catch {
          message = data;
        }
      } else if (data?.erreur) {
        message = data.erreur;
      } else if (e instanceof Error && e.message) {
        message = e.message;
      }
      setDownloadError(message);
    } finally {
      setDownloadingReport(false);
    }
  };

  const percent = Math.round(Math.min(1, Math.max(0, progress)) * 100);

  const itemRows = job?.items.map((item, index) => (
    <View key={item.id} style={[styles.item, index > 0 && styles.itemDivider]}>
      <View style={styles.itemHeader}>
        <Text style={styles.numero} numberOfLines={1}>{item.numeroSim}</Text>
        <Badge label={ITEM_LABELS[item.status]} tone={ITEM_TONES[item.status]} icon={ITEM_ICONS[item.status]} />
      </View>
      <Text style={styles.attempts}>
        {item.attemptCount > 1
          ? `${item.attemptCount} tentatives`
          : `${item.attemptCount} tentative`}
      </Text>
      {item.errorMessage ? (
        <Text style={styles.agentError}>Erreur de l’agent : {item.errorMessage}</Text>
      ) : null}
    </View>
  ));

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={canDismiss ? onDismiss : undefined}
        contentContainerStyle={[
          styles.modal,
          { width: Math.min(560, windowWidth - 32), maxHeight: windowHeight - 32 },
        ]}
      >
        <View style={styles.titleRow}>
          <View style={styles.titleContent}>
            <Text style={styles.title} accessibilityRole="header">Suivi des recharges SIM</Text>
            {job ? (
              <View style={styles.subtitleRow}>
                <Text style={styles.subtitle}>Tâche n°{job.id}</Text>
                <Badge label={JOB_LABELS[job.status]} tone={JOB_TONES[job.status]} dot />
              </View>
            ) : null}
          </View>
          {(creating || (polling && !terminal)) && (
            <ActivityIndicator size="small" color={colors.primary} />
          )}
        </View>

        {creating && !job ? (
          <View style={styles.loadingState}>
            <ActivityIndicator color={colors.primary} />
            <Text style={styles.loadingText}>Mise en file d’attente des recharges…</Text>
          </View>
        ) : null}

        {error || downloadError ? (
          <View style={styles.errorBox}>
            <MaterialCommunityIcons name="alert-circle-outline" size={18} color={colors.danger} />
            <Text style={styles.errorText}>{downloadError ?? error}</Text>
          </View>
        ) : null}

        {job ? (
          <View style={styles.progressSection}>
            <View style={styles.progressHeader}>
              <Text style={styles.progressLabel}>Progression globale</Text>
              <Text style={styles.progressValue}>
                {completedCount}/{job.totalCount} · {percent} %
              </Text>
            </View>
            <View
              style={styles.progressTrack}
              accessibilityRole="progressbar"
              accessibilityValue={{ min: 0, max: job.totalCount, now: completedCount }}
            >
              <View
                style={[
                  styles.progressFill,
                  { width: `${percent}%` as const, backgroundColor: progressColor(colors, job.status) },
                ]}
              />
            </View>

            <View style={styles.summaryRow}>
              <Badge label={`En file : ${job.pendingCount}`} tone="neutral" icon="clock-outline" />
              <Badge label={`En cours : ${job.inProgressCount}`} tone="info" icon="sync" />
              <Badge label={`Réussies : ${job.succeededCount}`} tone="success" icon="check" />
              <Badge label={`Échouées : ${job.failedCount}`} tone="danger" icon="close" />
            </View>
          </View>
        ) : null}

        {job ? (
          <View style={styles.itemsSection}>
            <Text style={styles.sectionLabel}>Détail par carte ({job.items.length})</Text>
            {Platform.OS === 'web' ? (
              <View style={[styles.items, styles.itemsWeb, { maxHeight: listHeight }]}>{itemRows}</View>
            ) : (
              <ScrollView style={[styles.items, { maxHeight: listHeight }]} nestedScrollEnabled>
                {itemRows}
              </ScrollView>
            )}
          </View>
        ) : null}

        <View style={styles.actions}>
          <Button
            mode="text"
            textColor={colors.textPrimary}
            disabled={!canDismiss || creating}
            onPress={onDismiss}
          >
            Fermer
          </Button>
          {!terminal && job ? (
            <Button
              mode="outlined"
              icon="stop-circle-outline"
              textColor={colors.danger}
              style={creating ? undefined : styles.dangerOutline}
              loading={creating}
              disabled={creating}
              onPress={onStop}
            >
              Arrêter la recharge
            </Button>
          ) : null}
          {pollingPaused && !terminal ? (
            <Button
              mode="contained"
              icon="refresh"
              onPress={onResumePolling}
            >
              Reprendre le suivi
            </Button>
          ) : null}
          {terminal && failedSimIds.length > 0 ? (
            <Button
              mode="outlined"
              icon="refresh"
              textColor={colors.textPrimary}
              loading={creating}
              disabled={creating}
              onPress={() => onRetryFailed(failedSimIds)}
            >
              Réessayer les échecs
            </Button>
          ) : null}
          {terminal ? (
            <Button
              mode="contained"
              icon="download"
              loading={downloadingReport}
              disabled={downloadingReport || creating}
              onPress={() => void handleDownloadReport()}
            >
              Télécharger le rapport CSV
            </Button>
          ) : null}
        </View>
        {terminal ? (
          <View style={styles.emailNote}>
            <MaterialCommunityIcons name="email-outline" size={14} color={colors.textSecondary} />
            <Text style={styles.emailNoteText}>Le rapport CSV est aussi envoyé par e-mail.</Text>
          </View>
        ) : null}
      </Modal>
    </Portal>
  );
}

const useStyles = makeStyles((c) => ({
  modal: {
    alignSelf: 'center',
    maxWidth: 560,
    backgroundColor: c.surface,
    borderRadius: RADIUS.xl,
    padding: 24,
    justifyContent: 'flex-start',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    flexGrow: 0,
    flexShrink: 0,
  },
  titleContent: { flexGrow: 1, flexShrink: 1 },
  title: { ...TYPOGRAPHY.title, color: c.textPrimary },
  subtitleRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  subtitle: { fontSize: 14, lineHeight: 20, color: c.textSecondary },
  loadingState: { alignItems: 'center', paddingVertical: 28, gap: 12 },
  loadingText: { ...TYPOGRAPHY.body, color: c.textSecondary },
  errorBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: 16,
    padding: 12,
    borderRadius: RADIUS.md,
    backgroundColor: c.dangerSoft,
  },
  errorText: { flex: 1, color: c.danger, fontSize: 14, lineHeight: 20 },
  progressSection: { marginTop: 20, flexGrow: 0, flexShrink: 0 },
  progressHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 8,
  },
  progressLabel: { ...TYPOGRAPHY.label, color: c.textPrimary },
  progressValue: { fontSize: 14, fontWeight: '600', color: c.textSecondary, fontVariant: ['tabular-nums' as const] },
  progressTrack: {
    height: 8,
    width: '100%',
    borderRadius: RADIUS.pill,
    backgroundColor: c.mode === 'dark' ? c.border : c.divider,
    overflow: 'hidden',
  },
  progressFill: { height: '100%', borderRadius: RADIUS.pill },
  summaryRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 14,
  },
  itemsSection: { marginTop: 20, flexGrow: 0, flexShrink: 0 },
  sectionLabel: { ...TYPOGRAPHY.label, color: c.textPrimary, marginBottom: 8 },
  items: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    borderWidth: 1,
    borderColor: c.divider,
    borderRadius: RADIUS.md,
  },
  // Web: the bounded list scrolls with plain CSS overflow (vertical only).
  itemsWeb: { overflowX: 'hidden', overflowY: 'auto' } as object,
  item: { paddingVertical: 12, paddingHorizontal: 14 },
  itemDivider: { borderTopWidth: 1, borderTopColor: c.divider },
  itemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  numero: { flex: 1, fontSize: 15, fontWeight: '600', color: c.textPrimary, fontVariant: ['tabular-nums' as const] },
  attempts: { ...TYPOGRAPHY.caption, marginTop: 4, color: c.textSecondary },
  agentError: { marginTop: 6, color: c.danger, fontSize: 13, lineHeight: 18 },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: c.divider,
    flexGrow: 0,
    flexShrink: 0,
  },
  dangerOutline: { borderColor: c.danger },
  emailNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 6,
    marginTop: 10,
  },
  emailNoteText: { ...TYPOGRAPHY.caption, color: c.textSecondary },
}));
